import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Plus, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { IAutomationVariable, AutomationVariableValue } from '@frontend/app/types/automations/condition/AutomationVariable';
import { ComboOperator, ComparisonOperator, MappedOperator } from '@frontend/app/types/automations/condition/Operator';
import { ConditionType } from '@frontend/app/types/automations/condition/Condition';
import { FieldType, AutomationVariableType } from '@frontend/app/types/globalTypes';
import { GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { convertToTitleCase } from '@frontend/app/utils/strings';
import {
  getGroupsForVariable,
  getFieldsForGroup,
} from '@frontend/app/containers/Projects/AutomationConfig/utils/condition';
import { getOperatorOptions } from '@frontend/app/containers/Projects/AutomationConfig/utils';
import { isEmailAddressValid } from '@common';
import { isArray, isEmpty, isNil, isString } from 'lodash';
import { FormattedOption } from '@frontend/app/containers/Projects/AutomationConfig/types';

import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { getConditionOperatorsByFieldType, formatConditionValue } from '../utils/conditionUtils';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Calendar } from '@/shadcn/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { cn } from '@/shadcn/lib/utils';
import { AutomationNodeV2 } from '../types';

// V2-specific function to get allowed automation variables (includes additional types for V2)
const getAllowedAutomationVariablesV2 = (automationVariables: IAutomationVariable[], isStaOffer?: boolean): IAutomationVariable[] => {
  const allowedTypes = [AutomationVariableType.MEMBER, AutomationVariableType.AGREEMENT, AutomationVariableType.PFA_ORDER];

  // For STA Offer automations, also include REWARD_INFO variable type (for compatibility)
  if (isStaOffer) {
    allowedTypes.push(AutomationVariableType.REWARD_INFO);
  }

  return automationVariables.filter((variable) => allowedTypes.includes(variable.type));
};

// Type definitions for condition nodes
type ConditionLogicalOp = 'AND' | 'OR' | 'NOT';
type ConditionCompareOp =
  | 'EQUALS' | 'NOT_EQUALS' | 'GREATER_THAN' | 'LESS_THAN'
  | 'CONTAINS' | 'HAS_VALUE' | 'STARTS_WITH' | 'ENDS_WITH'
  | 'IN_THE_LAST' | 'IN_THE_NEXT' | 'MORE_THAN_AGO'
  | 'GREATER_THAN_EQUALS' | 'LESS_THAN_EQUALS';

export type ConditionNode =
  | { type: 'LOGICAL'; op: ConditionLogicalOp; locked?: boolean; children: ConditionNode[] }
  | { type: 'COMPARISON'; op: ConditionCompareOp; value?: AutomationVariableValue; variableId?: string; attribute?: string; locked?: boolean };

// Define the structure for individual conditions
interface AutomationConditionV2 {
  type: ConditionType.COMPARISON;
  attribute: string;
  mappedOperator: MappedOperator;
  op: MappedOperator;
  value: AutomationVariableValue;
  variableId: string;
  locked?: boolean;
  uuid: string;
  parentId?: string;
}

// Convert AutomationConditionV2 to metadata condition format
const convertToMetadataCondition = (
  conditions: AutomationConditionV2[],
  logicalOperator: 'AND' | 'OR' = 'AND'
) => {
  if (conditions.length === 0) return null;

  // Helper function to wrap NOT operators in LOGICAL NOT structure (matching V1 behavior)
  const notWrapper = (condition: AutomationConditionV2, baseOp: 'CONTAINS' | 'HAS_VALUE' | 'EQUALS' | 'IN_THE_LAST' | 'IN_THE_NEXT' | 'MORE_THAN_AGO' | 'STARTS_WITH' | 'ENDS_WITH') => ({
    type: 'LOGICAL' as const,
    op: 'NOT' as const,
    locked: false,
    children: [{
      type: 'COMPARISON' as const,
      op: baseOp,
      value: condition.value,
      variableId: condition.variableId,
      attribute: condition.attribute,
      locked: false,
    }],
  });

  // Convert a single condition to metadata format
  const convertSingleCondition = (condition: AutomationConditionV2) => {
    // Handle NOT operators by wrapping in LOGICAL NOT structure
    switch (condition.op) {
      case ComboOperator.NOT_CONTAINS:
        return notWrapper(condition, 'CONTAINS');
      case ComboOperator.IS_NULL:
        return notWrapper(condition, 'HAS_VALUE');
      case ComboOperator.NOT_EQUALS:
        return notWrapper(condition, 'EQUALS');
      case ComboOperator.NOT_IN_THE_LAST:
        return notWrapper(condition, 'IN_THE_LAST');
      case ComboOperator.NOT_IN_THE_NEXT:
        return notWrapper(condition, 'IN_THE_NEXT');
      case ComboOperator.NOT_MORE_THAN_AGO:
        return notWrapper(condition, 'MORE_THAN_AGO');
      case ComboOperator.NOT_STARTS_WITH:
        return notWrapper(condition, 'STARTS_WITH');
      case ComboOperator.NOT_ENDS_WITH:
        return notWrapper(condition, 'ENDS_WITH');
      case ComboOperator.BETWEEN:
        // BETWEEN is special - it maps to a LOGICAL AND with two COMPARISON children
        if (!Array.isArray(condition.value) || condition.value.length !== 2) {
          throw new Error('Between condition should have two values');
        }
        return {
          type: 'LOGICAL' as const,
          op: 'AND' as const,
          locked: false,
          children: [
            {
              type: 'COMPARISON' as const,
              op: 'GREATER_THAN_EQUALS' as const,
              value: condition.value[0],
              variableId: condition.variableId,
              attribute: condition.attribute,
              locked: false,
            },
            {
              type: 'COMPARISON' as const,
              op: 'LESS_THAN_EQUALS' as const,
              value: condition.value[1],
              variableId: condition.variableId,
              attribute: condition.attribute,
              locked: false,
            },
          ],
        };
      default: {
        // Regular operators - direct COMPARISON mapping
        let metadataOp:
          | 'EQUALS' | 'NOT_EQUALS' | 'GREATER_THAN' | 'LESS_THAN'
          | 'CONTAINS' | 'HAS_VALUE' | 'STARTS_WITH' | 'ENDS_WITH'
          | 'IN_THE_LAST' | 'IN_THE_NEXT' | 'MORE_THAN_AGO'
          | 'GREATER_THAN_EQUALS' | 'LESS_THAN_EQUALS';
        switch (condition.op) {
          case ComparisonOperator.EQUALS:
            metadataOp = 'EQUALS';
            break;
          case ComparisonOperator.GREATER_THAN:
            metadataOp = 'GREATER_THAN';
            break;
          case ComparisonOperator.GREATER_THAN_EQUALS:
            metadataOp = 'GREATER_THAN_EQUALS';
            break;
          case ComparisonOperator.LESS_THAN:
            metadataOp = 'LESS_THAN';
            break;
          case ComparisonOperator.LESS_THAN_EQUALS:
            metadataOp = 'LESS_THAN_EQUALS';
            break;
          case ComparisonOperator.CONTAINS:
            metadataOp = 'CONTAINS';
            break;
          case ComparisonOperator.HAS_VALUE:
            metadataOp = 'HAS_VALUE';
            break;
          case ComparisonOperator.STARTS_WITH:
            metadataOp = 'STARTS_WITH';
            break;
          case ComparisonOperator.ENDS_WITH:
            metadataOp = 'ENDS_WITH';
            break;
          case ComparisonOperator.IN_THE_LAST:
            metadataOp = 'IN_THE_LAST';
            break;
          case ComparisonOperator.IN_THE_NEXT:
            metadataOp = 'IN_THE_NEXT';
            break;
          case ComparisonOperator.MORE_THAN_AGO:
            metadataOp = 'MORE_THAN_AGO';
            break;
          default:
            metadataOp = 'EQUALS';
        }

        return {
          type: 'COMPARISON' as const,
          op: metadataOp,
          value: condition.value,
          variableId: condition.variableId,
          attribute: condition.attribute,
          locked: false,
        };
      }
    }
  };

  if (conditions.length === 1) {
    // Single condition - return the converted condition directly
    return convertSingleCondition(conditions[0]);
  }

  // Multiple conditions - wrap in LOGICAL structure
  return {
    type: 'LOGICAL' as const,
    op: logicalOperator,
    children: conditions.map((condition) => convertSingleCondition(condition)),
    locked: false,
  };
};

// Helper function to format condition as readable text
const formatConditionText = (
  condition: AutomationConditionV2,
  conditionOptions: GetNodeOptions_getNodeOptions_conditionOptions[]
): string => {
  const field = conditionOptions.find((field) => field.id === condition.attribute);
  const operator = condition.mappedOperator || condition.op;

  // Get the display option for the operator
  const displayOption = field ? getConditionOperatorsByFieldType(field.attributeType, operator) : null;

  // Format the value using the shared utility
  const formatValue = () => formatConditionValue(condition.value, operator, field);

  // Build the text
  const fieldName = field?.displayName || 'Unknown Field';
  const operatorText = displayOption?.inlineLabel || operator;
  const valueText = formatValue();

  if (operator === ComparisonOperator.HAS_VALUE || operator === ComboOperator.IS_NULL) {
    return `${fieldName} ${valueText}`;
  }

  // Special formatting for boolean fields to improve grammar
  if (field?.attributeType === FieldType.BOOLEAN &&
      (operator === ComparisonOperator.EQUALS || operator === ComboOperator.NOT_EQUALS)) {
    return `${fieldName}: ${valueText}`;
  }

  return `${fieldName} ${operatorText} ${valueText}`;
};

interface ConditionFormV2Props {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onClose: () => void;
  automationVariables: IAutomationVariable[];
  conditionOptions: GetNodeOptions_getNodeOptions_conditionOptions[];
  isStaOffer?: boolean;
}

export const ConditionFormV2Enhanced: React.FC<ConditionFormV2Props> = ({
  selectedNode,
  onUpdateNode,
  onClose,
  automationVariables,
  conditionOptions,
  isStaOffer,
}) => {
  const offerContext = useOfferDetailsContext();
  const isShopifyConnected = isStaOffer ? (offerContext?.isShopifyConnected || false) : false;
  const formattedOptions = useMemo<FormattedOption[]>(() =>
    conditionOptions.map((option) => ({
      ...option,
      group: convertToTitleCase(option.group),
    })), [conditionOptions]
  );

  // State for the list of conditions
  const [conditions, setConditions] = useState<AutomationConditionV2[]>([]);
  const [logicalOperator, setLogicalOperator] = useState<'AND' | 'OR'>(() => {
    // Initialize logical operator from metadata if available
    const existingCondition = selectedNode.metadata.condition;
    if (existingCondition && (existingCondition.type === ConditionType.LOGICAL)) {
      return existingCondition.op === 'OR' ? 'OR' : 'AND';
    }
    return 'AND';
  });

  // State for the condition being edited
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null);

  // Hierarchical selection state (like V1)
  const [selectedAutomationVariable, setSelectedAutomationVariable] = useState<IAutomationVariable | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groupsForVariable, setGroupsForVariable] = useState<string[]>([]);
  const [fieldsForGroup, setFieldsForGroup] = useState<FormattedOption[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [operators, setOperators] = useState<Array<{ value: MappedOperator; operatorLabel: string }>>([]);
  const [selectedOperator, setSelectedOperator] = useState<MappedOperator | null>(null);
  const [value, setValue] = useState<AutomationVariableValue>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const selectedField = fieldsForGroup.find((field) => field.id === selectedFieldId);
  const isAddingNewCondition = editingConditionId === 'new';
  const isEditingExisting = editingConditionId !== null && editingConditionId !== 'new';

  // Store locked conditions separately (not shown in UI, but preserved)
  const [lockedConditions, setLockedConditions] = useState<any[]>([]);

  // Initialize conditions from metadata
  useEffect(() => {
    const existingChildren = selectedNode.metadata.children || [];
    const existingCondition = selectedNode.metadata.condition;

    let initialConditions: AutomationConditionV2[] = [];
    const initialLockedConditions: any[] = [];
    let initialLogicalOp: 'AND' | 'OR' = 'AND';

    // Load from children array first (priority)
    if (existingChildren.length > 0) {
      // Separate locked and unlocked conditions
      existingChildren.forEach((child: AutomationConditionV2) => {
        if (child && child.type === ConditionType.COMPARISON) {
          if (child.locked === true) {
            initialLockedConditions.push(child);
          } else {
            initialConditions.push({
              ...child,
              parentId: child.parentId || selectedNode.metadata.uuid || selectedNode.id,
            });
          }
        }
      });

      // Also check for logical operator in the main condition if children exist
      if (existingCondition && existingCondition.type === ConditionType.LOGICAL) {
        initialLogicalOp = existingCondition.op === 'OR' ? 'OR' : 'AND';
      }
    } else if (existingCondition && existingCondition.type === 'COMPARISON') {
      const singleCondition: AutomationConditionV2 = {
        type: ConditionType.COMPARISON,
        attribute: existingCondition.attribute || '',
        mappedOperator: ComparisonOperator.EQUALS, // Default fallback
        op: (() => {
          switch (existingCondition.op) {
            case 'EQUALS': return ComparisonOperator.EQUALS;
            case 'NOT_EQUALS': return ComboOperator.NOT_EQUALS;
            case 'GREATER_THAN': return ComparisonOperator.GREATER_THAN;
            case 'LESS_THAN': return ComparisonOperator.LESS_THAN;
            case 'CONTAINS': return ComparisonOperator.CONTAINS;
            default: return ComparisonOperator.EQUALS;
          }
        })(),
        value: existingCondition.value || null,
        variableId: existingCondition.variableId || '',
        locked: existingCondition.locked || false,
        uuid: `condition-${Date.now()}`,
        parentId: selectedNode.metadata.uuid || selectedNode.id,
      };

      // Check if single condition is locked
      if (existingCondition.locked === true) {
        initialLockedConditions.push(singleCondition);
      } else {
        initialConditions = [singleCondition];
      }
    } else if (existingCondition && existingCondition.type === ConditionType.LOGICAL) {
      initialLogicalOp = existingCondition.op === 'OR' ? 'OR' : 'AND';
      if (existingCondition.children) {
        existingCondition.children.forEach((child: ConditionNode, index: number) => {
          // Handle LOGICAL children (NOT operations or BETWEEN)
          if (child.type === 'LOGICAL') {
            if (child.op === 'NOT' && child.children && child.children.length === 1) {
              // Handle NOT operations
              const notChild = child.children[0];
              if (notChild.type === 'COMPARISON') {
                const mappedOperator = (() => {
                  switch (notChild.op) {
                    case ComparisonOperator.CONTAINS: return ComboOperator.NOT_CONTAINS;
                    case ComparisonOperator.HAS_VALUE: return ComboOperator.IS_NULL;
                    case ComparisonOperator.EQUALS: return ComboOperator.NOT_EQUALS;
                    case ComparisonOperator.IN_THE_LAST: return ComboOperator.NOT_IN_THE_LAST;
                    case ComparisonOperator.IN_THE_NEXT: return ComboOperator.NOT_IN_THE_NEXT;
                    case ComparisonOperator.MORE_THAN_AGO: return ComboOperator.NOT_MORE_THAN_AGO;
                    case ComparisonOperator.STARTS_WITH: return ComboOperator.NOT_STARTS_WITH;
                    case ComparisonOperator.ENDS_WITH: return ComboOperator.NOT_ENDS_WITH;
                    default: return ComboOperator.NOT_EQUALS;
                  }
                })();

                const conditionData: AutomationConditionV2 = {
                  type: ConditionType.COMPARISON,
                  attribute: notChild.attribute || '',
                  mappedOperator,
                  op: mappedOperator,
                  value: notChild.value || null,
                  variableId: notChild.variableId || '',
                  locked: child.locked || false,
                  uuid: `condition-${Date.now()}-${index}`,
                  parentId: selectedNode.metadata.uuid || selectedNode.id,
                };

                if (child.locked === true) {
                  initialLockedConditions.push(conditionData);
                } else {
                  initialConditions.push(conditionData);
                }
              }
            } else if (child.op === 'AND' && child.children && child.children.length === 2) {
              // Handle BETWEEN conditions (AND with GREATER_THAN_EQUALS and LESS_THAN_EQUALS)
              const firstChild = child.children[0];
              const secondChild = child.children[1];

              if (firstChild.type === 'COMPARISON' && secondChild.type === 'COMPARISON' &&
                  firstChild.op === ComparisonOperator.GREATER_THAN_EQUALS && secondChild.op === ComparisonOperator.LESS_THAN_EQUALS &&
                  firstChild.attribute === secondChild.attribute) {
                const conditionData: AutomationConditionV2 = {
                  type: ConditionType.COMPARISON,
                  attribute: firstChild.attribute || '',
                  mappedOperator: ComboOperator.BETWEEN,
                  op: ComboOperator.BETWEEN,
                  value: [firstChild.value, secondChild.value] as AutomationVariableValue,
                  variableId: firstChild.variableId || '',
                  locked: child.locked || false,
                  uuid: `condition-${Date.now()}-${index}`,
                  parentId: selectedNode.metadata.uuid || selectedNode.id,
                };

                if (child.locked === true) {
                  initialLockedConditions.push(conditionData);
                } else {
                  initialConditions.push(conditionData);
                }
              }
            }
          } else if (child.type === 'COMPARISON') {
            const conditionData: AutomationConditionV2 = {
              type: ConditionType.COMPARISON,
              attribute: child.attribute || '',
              mappedOperator: (() => {
                switch (child.op) {
                  case 'EQUALS': return ComparisonOperator.EQUALS;
                  case 'GREATER_THAN': return ComparisonOperator.GREATER_THAN;
                  case 'LESS_THAN': return ComparisonOperator.LESS_THAN;
                  case 'GREATER_THAN_EQUALS': return ComparisonOperator.GREATER_THAN_EQUALS;
                  case 'LESS_THAN_EQUALS': return ComparisonOperator.LESS_THAN_EQUALS;
                  case 'CONTAINS': return ComparisonOperator.CONTAINS;
                  case 'HAS_VALUE': return ComparisonOperator.HAS_VALUE;
                  case 'STARTS_WITH': return ComparisonOperator.STARTS_WITH;
                  case 'ENDS_WITH': return ComparisonOperator.ENDS_WITH;
                  case 'IN_THE_LAST': return ComparisonOperator.IN_THE_LAST;
                  case 'IN_THE_NEXT': return ComparisonOperator.IN_THE_NEXT;
                  case 'MORE_THAN_AGO': return ComparisonOperator.MORE_THAN_AGO;
                  default: return ComparisonOperator.EQUALS;
                }
              })(),
              op: (() => {
                switch (child.op) {
                  case 'EQUALS': return ComparisonOperator.EQUALS;
                  case 'GREATER_THAN': return ComparisonOperator.GREATER_THAN;
                  case 'LESS_THAN': return ComparisonOperator.LESS_THAN;
                  case 'GREATER_THAN_EQUALS': return ComparisonOperator.GREATER_THAN_EQUALS;
                  case 'LESS_THAN_EQUALS': return ComparisonOperator.LESS_THAN_EQUALS;
                  case 'CONTAINS': return ComparisonOperator.CONTAINS;
                  case 'HAS_VALUE': return ComparisonOperator.HAS_VALUE;
                  case 'STARTS_WITH': return ComparisonOperator.STARTS_WITH;
                  case 'ENDS_WITH': return ComparisonOperator.ENDS_WITH;
                  case 'IN_THE_LAST': return ComparisonOperator.IN_THE_LAST;
                  case 'IN_THE_NEXT': return ComparisonOperator.IN_THE_NEXT;
                  case 'MORE_THAN_AGO': return ComparisonOperator.MORE_THAN_AGO;
                  default: return ComparisonOperator.EQUALS;
                }
              })(),
              value: child.value || null,
              variableId: child.variableId || '',
              locked: child.locked || false,
              uuid: `condition-${Date.now()}-${index}`,
              parentId: selectedNode.metadata.uuid || selectedNode.id,
            };

            if (child.locked === true) {
              initialLockedConditions.push(conditionData);
            } else {
              initialConditions.push(conditionData);
            }
          }
        });
      }
    }

    setConditions(initialConditions);
    setLockedConditions(initialLockedConditions);
    setLogicalOperator(initialLogicalOp);
  }, [selectedNode.metadata, selectedNode.id]);

  // Initialize form state for condition editing
  useEffect(() => {
    if (formattedOptions.length > 0 && (isAddingNewCondition || isEditingExisting)) {
      const allowedVars = getAllowedAutomationVariablesV2(automationVariables, isStaOffer);

      if (isEditingExisting) {
        // Load existing condition for editing with hierarchical setup
        const conditionToEdit = conditions.find((c) => c.uuid === editingConditionId);
        if (conditionToEdit) {
          // 1. Set the variable
          const selectedVar = allowedVars.find((variable) => variable.id === conditionToEdit.variableId);
          setSelectedAutomationVariable(selectedVar || null);

          if (selectedVar) {
            // 2. Set the groups for this variable type
            const groups = getGroupsForVariable(selectedVar.type, formattedOptions);
            setGroupsForVariable(groups);

            // 3. Find the group that contains the field
            const fieldData = formattedOptions.find((field) => field.id === conditionToEdit.attribute);
            if (fieldData) {
              setSelectedGroup(fieldData.group);

              // 4. Set the fields for this group
              const fields = getFieldsForGroup(fieldData.group, formattedOptions, selectedVar.type);
              setFieldsForGroup(fields);

              // 5. Set the selected field
              setSelectedFieldId(conditionToEdit.attribute);

              // 6. Set operators and values
              const ops = getOperatorOptions(fieldData.attributeType, fieldData.choices);
              setOperators(ops);
              setSelectedOperator(conditionToEdit.mappedOperator || conditionToEdit.op);
              setValue(conditionToEdit.value);
            }
          }
        }
      } else {
        // Initialize new condition - show all available groups from all variable types
        const allGroups = new Set<string>();
        allowedVars.forEach((variable) => {
          const groups = getGroupsForVariable(variable.type, formattedOptions);
          groups.forEach((group) => allGroups.add(group));
        });

        setGroupsForVariable(Array.from(allGroups).sort());

        // Don't auto-select a variable - let user choose
        setSelectedAutomationVariable(null);

        // Reset other selections
        setSelectedGroup(null);
        setFieldsForGroup([]);
        setSelectedFieldId(null);
        setOperators([]);
        setSelectedOperator(null);
        setValue(null);
      }
    }
  }, [automationVariables, formattedOptions, editingConditionId, isAddingNewCondition, isEditingExisting, conditions, isStaOffer]);

  // Hierarchical selection handlers (updated for V2 to support multiple variable types)
  const onChangeGroup = (group: string) => {
    setSelectedGroup(group);

    if (group) {
      // Get fields from ALL available variable types for this group
      const allowedVars = getAllowedAutomationVariablesV2(automationVariables, isStaOffer);
      const allFields = [];

      allowedVars.forEach((variable) => {
        const fields = getFieldsForGroup(group, formattedOptions, variable.type);
        allFields.push(...fields);
      });

      // Remove duplicates based on field id
      const uniqueFields = allFields.filter((field, index, self) =>
        index === self.findIndex((f) => f.id === field.id)
      );

      setFieldsForGroup(uniqueFields);
    } else {
      setFieldsForGroup([]);
    }

    // Reset subsequent selections
    setSelectedFieldId(null);
    setOperators([]);
    setSelectedOperator(null);
    setValue(null);
    setValidationError('');
  };

  const onChangeField = (fieldId: string) => {
    // Use fieldsForGroup instead of formattedOptions for hierarchical selection
    const field = fieldsForGroup.find((f) => f.id === fieldId);
    if (!field) return;

    setSelectedFieldId(fieldId);

    // Auto-select the appropriate automation variable based on the field's variable type
    const allowedVars = getAllowedAutomationVariablesV2(automationVariables, isStaOffer);
    const matchingVariable = allowedVars.find((variable) => variable.type === field.variableType);
    if (matchingVariable) {
      setSelectedAutomationVariable(matchingVariable);
    }

    const ops = getOperatorOptions(field.attributeType, field.choices);
    setOperators(ops);

    // Reset operator and value when field changes
    setSelectedOperator(null);
    setValue(null);
    setValidationError('');
  };

  const onChangeOperator = (operator: MappedOperator) => {
    setSelectedOperator(operator);
    if (operator === ComparisonOperator.HAS_VALUE || operator === ComboOperator.IS_NULL) {
      setValue(null);
    }
    setValidationError('');
  };

  const validateCondition = () => {
    const invalidValueError = 'Please enter a valid value';
    const noValueError = 'Please enter a value';
    const noVariableError = 'Please select a variable';
    const negativeNumberError = 'Please enter a positive number';
    const betweenValueError = 'Please enter a valid range';
    const invalidEmailFormatError = 'Please enter a valid email';
    const noError = '';
    let errorString = noError;

    if (isNil(selectedFieldId)) {
      errorString = noVariableError;
    }

    if (selectedOperator && errorString === noError) {
      switch (selectedOperator) {
        case ComparisonOperator.CONTAINS:
        case ComboOperator.NOT_CONTAINS:
          if (isNil(value)) errorString = noValueError;
          if (value === '') errorString = noValueError;
          if (isArray(value) && value.length === 0) errorString = noValueError;
          break;
        case ComparisonOperator.HAS_VALUE:
        case ComboOperator.IS_NULL:
          // V1 logic: HAS_VALUE/IS_NULL should not have values
          if (!isNil(value)) errorString = noValueError;
          break;
        case ComparisonOperator.EQUALS:
        case ComboOperator.NOT_EQUALS:
          if (isNil(value)) errorString = noValueError;
          if (value === '') errorString = noValueError;
          if (typeof value === 'string' && value !== '' && selectedField?.attributeType === FieldType.EMAIL && !isEmailAddressValid(value)) {
            errorString = invalidEmailFormatError;
          }
          if (isArray(value) && isEmpty(value)) errorString = noValueError;
          break;
        case ComparisonOperator.IN_THE_LAST:
        case ComparisonOperator.IN_THE_NEXT:
        case ComparisonOperator.MORE_THAN_AGO:
        case ComboOperator.NOT_IN_THE_LAST:
        case ComboOperator.NOT_IN_THE_NEXT:
        case ComboOperator.NOT_MORE_THAN_AGO:
          if (isNil(value)) errorString = noValueError;
          if (Number(value) < 0) errorString = negativeNumberError;
          break;
        case ComparisonOperator.STARTS_WITH:
        case ComparisonOperator.ENDS_WITH:
        case ComboOperator.NOT_STARTS_WITH:
        case ComboOperator.NOT_ENDS_WITH:
          if (isNil(value)) errorString = noValueError;
          if (!isString(value)) errorString = noValueError;
          if (value === '') errorString = noValueError;
          break;
        case ComparisonOperator.GREATER_THAN:
        case ComparisonOperator.GREATER_THAN_EQUALS:
        case ComparisonOperator.LESS_THAN:
        case ComparisonOperator.LESS_THAN_EQUALS:
          if (isNil(value)) errorString = noValueError;
          break;
        case ComboOperator.BETWEEN:
          if (isNil(value)) errorString = noValueError;
          if (!isArray(value)) errorString = invalidValueError;
          if (isArray(value) && value.length !== 2) errorString = invalidValueError;
          if (isArray(value) && value.length === 2) {
            // Check if both values are provided
            if (value[0] === null || value[1] === null) errorString = noValueError;
            // Check if min < max for numbers and dates
            else if (typeof value[0] === 'number' && typeof value[1] === 'number' && value[0] >= value[1]) {
              errorString = betweenValueError;
            } else if (value[0] instanceof Date && value[1] instanceof Date && value[0] >= value[1]) {
              errorString = betweenValueError;
            } else if (typeof value[0] === 'string' && typeof value[1] === 'string' &&
                     value[0].includes('T') && value[1].includes('T') && // ISO date strings
                     new Date(value[0]) >= new Date(value[1])) {
              errorString = betweenValueError;
            } else if (typeof value[0] !== typeof value[1]) {
              errorString = invalidValueError;
            }
          }
          break;
      }
    }

    setValidationError(errorString);
    return errorString === noError;
  };

  // Check if all required fields are filled
  const isFormComplete = () => {
    if (!selectedFieldId || !selectedOperator) return false;

    // Check if value is required for this operator
    if (selectedOperator === ComparisonOperator.HAS_VALUE || selectedOperator === ComboOperator.IS_NULL) {
      return true; // These operators don't need a value
    }

    // Special handling for BETWEEN operator
    if (selectedOperator === ComboOperator.BETWEEN) {
      return Array.isArray(value) && value.length === 2 && value[0] !== null && value[1] !== null;
    }

    // For other operators, check if value is provided
    return value !== null && value !== undefined && value !== '';
  };

  const handleSaveCondition = () => {
    if (!validateCondition()) return;

    const newCondition: AutomationConditionV2 = {
      type: ConditionType.COMPARISON,
      attribute: selectedFieldId!,
      mappedOperator: selectedOperator!,
      op: selectedOperator!,
      value,
      variableId: selectedAutomationVariable?.id || '',
      locked: false,
      uuid: isEditingExisting ? editingConditionId! : `condition-${Date.now()}`,
      parentId: selectedNode.metadata.uuid || selectedNode.id,
    };

    if (isEditingExisting) {
      // Update existing condition
      setConditions((prev) => prev.map((c) =>
        (c.uuid === editingConditionId ? newCondition : c)
      ));
    } else {
      // Add new condition
      setConditions((prev) => [...prev, newCondition]);
    }

    // Reset form
    setEditingConditionId(null);
    setSelectedAutomationVariable(null);
    setSelectedFieldId(null);
    setSelectedOperator(null);
    setValue(null);
    setValidationError('');
  };

  const handleDeleteCondition = (conditionId: string) => {
    setConditions((prev) => prev.filter((c) => c.uuid !== conditionId));
    if (editingConditionId === conditionId) {
      setEditingConditionId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingConditionId(null);
    setValidationError('');
  };

  const handleDone = () => {
    // Merge unlocked (editable) conditions with locked conditions for saving
    const allConditionsForSave = [...conditions, ...lockedConditions];

    // Save all conditions to metadata with proper V1 structure
    const metadataCondition = convertToMetadataCondition(conditions, logicalOperator);

    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        children: allConditionsForSave, // Include both locked and unlocked conditions
        condition: metadataCondition, // Only unlocked conditions in the main condition structure
        // Add uuid if not present for V1 compatibility
        uuid: selectedNode.metadata.uuid || selectedNode.id,
      },
    });

    onClose();
  };

  const renderValueInput = () => {
    // Don't show value input for operators that don't need values
    if (!selectedOperator || selectedOperator === ComparisonOperator.HAS_VALUE || selectedOperator === ComboOperator.IS_NULL) {
      return null;
    }

    if (!selectedField) {
      return null;
    }

    // Boolean fields - show Yes/No dropdown
    if (selectedField.attributeType === FieldType.BOOLEAN) {
      const operators: MappedOperator[] = [ComparisonOperator.EQUALS, ComboOperator.NOT_EQUALS];
      if (selectedOperator && operators.includes(selectedOperator)) {
        const currentBoolValue = typeof value === 'boolean' ? value.toString() : '';
        return (
          <Select value={currentBoolValue} onValueChange={(val) => setValue(val === 'true')}>
            <SelectTrigger className="w-full overflow-hidden max-w-full">
              <SelectValue placeholder="Select Yes or No..." className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        );
      }
    }

    // Text fields with predefined choices - show dropdown hide if isShopifyConnected is false
    if (selectedField.attributeType === FieldType.TEXT && selectedField.choices && selectedField.choices.length > 0) {
      const operators: MappedOperator[] = [ComparisonOperator.EQUALS, ComboOperator.NOT_EQUALS, ComparisonOperator.CONTAINS, ComboOperator.NOT_CONTAINS];
      if (selectedOperator && operators.includes(selectedOperator)) {
        const currentValue = value !== null && value !== undefined ? value.toString() : '';
        return (
          <Select value={currentValue} onValueChange={setValue}>
            <SelectTrigger className="w-full overflow-hidden max-w-full">
              <SelectValue placeholder="Select a value..." className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
            </SelectTrigger>
            <SelectContent>
              {selectedField.choices
                .filter((choice) => {
                  // Hide Shopify-dependent reward options when STA offer and Shopify is not connected
                  if (isStaOffer && !isShopifyConnected && (
                    choice.displayName === 'Free Product Reward' ||
                    choice.displayName === 'Discount Code Reward'
                  )) {
                    return false;
                  }
                  return true;
                })
                .map((choice) => (
                  <SelectItem key={choice.value} value={choice.value.toString()}>
                    {choice.displayName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );
      }
    }

    // Array fields - show multi-select dropdown
    if (selectedField.attributeType === FieldType.ARRAY && selectedField.choices && selectedField.choices.length > 0) {
      const operators: MappedOperator[] = [ComparisonOperator.CONTAINS, ComboOperator.NOT_CONTAINS, ComparisonOperator.EQUALS, ComboOperator.NOT_EQUALS];
      if (selectedOperator && operators.includes(selectedOperator)) {
        const currentValue = Array.isArray(value) && value.length > 0 ? value[0]?.toString() || '' :
          (value !== null && value !== undefined ? value.toString() : '');
        return (
          <Select value={currentValue} onValueChange={(val) => setValue([val])}>
            <SelectTrigger className="w-full overflow-hidden max-w-full">
              <SelectValue placeholder="Select a value..." className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
            </SelectTrigger>
            <SelectContent>
              {selectedField.choices
                .filter((choice) => {
                  // Hide Shopify-dependent reward options when STA offer and Shopify is not connected
                  if (isStaOffer && !isShopifyConnected && (
                    choice.displayName === 'Free Product Reward' ||
                    choice.displayName === 'Discount Code Reward'
                  )) {
                    return false;
                  }
                  return true;
                })
                .map((choice) => (
                  <SelectItem key={choice.value} value={choice.value.toString()}>
                    {choice.displayName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );
      }
    }

    // Between operator - show two number inputs for range
    if (selectedOperator === ComboOperator.BETWEEN && [FieldType.NUMBER, FieldType.PERCENTAGE].includes(selectedField.attributeType)) {
      const currentValue = Array.isArray(value) ? value : [null, null];
      return (
        <div className="flex items-center gap-2 w-full">
          <Input
            type="number"
            value={currentValue[0]?.toString() || ''}
            onChange={(e) => {
              const numValue = parseFloat(e.target.value);
              const newValue = isNaN(numValue) ? null : numValue;
              setValue([newValue, currentValue[1]] as AutomationVariableValue);
            }}
            placeholder="Min value"
            className="flex-1"
          />
          <span className="text-sm text-grey-5 font-medium">and</span>
          <Input
            type="number"
            value={currentValue[1]?.toString() || ''}
            onChange={(e) => {
              const numValue = parseFloat(e.target.value);
              const newValue = isNaN(numValue) ? null : numValue;
              setValue([currentValue[0], newValue] as AutomationVariableValue);
            }}
            placeholder="Max value"
            className="flex-1"
          />
        </div>
      );
    }

    // Number fields - show number input
    if ([FieldType.NUMBER, FieldType.PERCENTAGE].includes(selectedField.attributeType)) {
      const operators: MappedOperator[] = [
        ComparisonOperator.EQUALS,
        ComboOperator.NOT_EQUALS,
        ComparisonOperator.GREATER_THAN,
        ComparisonOperator.GREATER_THAN_EQUALS,
        ComparisonOperator.LESS_THAN,
        ComparisonOperator.LESS_THAN_EQUALS,
        ComparisonOperator.IN_THE_LAST,
        ComparisonOperator.IN_THE_NEXT,
        ComparisonOperator.MORE_THAN_AGO,
        ComboOperator.NOT_IN_THE_LAST,
        ComboOperator.NOT_IN_THE_NEXT,
        ComboOperator.NOT_MORE_THAN_AGO,
      ];
      if (selectedOperator && operators.includes(selectedOperator)) {
        return (
          <Input
            type="number"
            value={value?.toString() || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              setValue(isNaN(value) ? 0 : value);
            }}
            placeholder="Enter number..."
            className="w-full"
          />
        );
      }
    }

    // Date fields - Between operator for dates
    if (selectedOperator === ComboOperator.BETWEEN && selectedField.attributeType === FieldType.DATE) {
      const currentValue = Array.isArray(value) ? value : [null, null];
      return (
        <div className="flex items-center gap-2 w-full">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'flex-1 justify-start text-left font-normal',
                  !currentValue[0] && 'text-grey-4'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {currentValue[0] && typeof currentValue[0] === 'string' ? format(new Date(currentValue[0]), 'MMM do, yyyy') : 'Start date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-grey-0 pointer-events-auto user-select-text" align="start">
              <Calendar
                mode="single"
                selected={currentValue[0] && typeof currentValue[0] === 'string' ? new Date(currentValue[0]) : undefined}
                onSelect={(date) => {
                  const dateValue = date ? date.toISOString() : null;
                  setValue([dateValue, currentValue[1]] as AutomationVariableValue);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <span className="text-sm text-grey-5 font-medium">and</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'flex-1 justify-start text-left font-normal',
                  !currentValue[1] && 'text-grey-4'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {currentValue[1] && typeof currentValue[1] === 'string' ? format(new Date(currentValue[1]), 'MMM do, yyyy') : 'End date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-grey-0 pointer-events-auto user-select-text" align="start">
              <Calendar
                mode="single"
                selected={currentValue[1] && typeof currentValue[1] === 'string' ? new Date(currentValue[1]) : undefined}
                onSelect={(date) => {
                  const dateValue = date ? date.toISOString() : null;
                  setValue([currentValue[0], dateValue] as AutomationVariableValue);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    // Date fields - show date input (simplified for now)
    if (selectedField.attributeType === FieldType.DATE) {
      const dateOperators: MappedOperator[] = [
        ComparisonOperator.EQUALS,
        ComboOperator.NOT_EQUALS,
        ComparisonOperator.GREATER_THAN,
        ComparisonOperator.LESS_THAN,
      ];
      const numberOperators: MappedOperator[] = [
        ComparisonOperator.IN_THE_LAST,
        ComparisonOperator.IN_THE_NEXT,
        ComparisonOperator.MORE_THAN_AGO,
        ComboOperator.NOT_IN_THE_LAST,
        ComboOperator.NOT_IN_THE_NEXT,
        ComboOperator.NOT_MORE_THAN_AGO,
      ];

      if (selectedOperator && dateOperators.includes(selectedOperator)) {
        return (
          <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !value && 'text-grey-4'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value && typeof value === 'string' ? format(new Date(value), 'MMM do, yyyy') : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-grey-0 pointer-events-auto user-select-text" align="start">
              <Calendar
                mode="single"
                selected={value && typeof value === 'string' ? new Date(value) : undefined}
                onSelect={(date) => {
                  // Convert date to ISO string for consistent handling
                  const dateValue = date ? date.toISOString() : null;
                  setValue(dateValue);
                  setIsDatePickerOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );
      } else if (selectedOperator && numberOperators.includes(selectedOperator)) {
        return (
          <Input
            type="number"
            value={value?.toString() || ''}
            onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
            placeholder="Enter number of days..."
            className="w-full"
          />
        );
      }
    }

    // Text fields without choices - show text input
    if ([FieldType.TEXT, FieldType.EMAIL].includes(selectedField.attributeType)) {
      const operators: MappedOperator[] = [
        ComparisonOperator.EQUALS,
        ComboOperator.NOT_EQUALS,
        ComparisonOperator.CONTAINS,
        ComboOperator.NOT_CONTAINS,
        ComparisonOperator.STARTS_WITH,
        ComparisonOperator.ENDS_WITH,
        ComboOperator.NOT_STARTS_WITH,
        ComboOperator.NOT_ENDS_WITH,
      ];
      if (selectedOperator && operators.includes(selectedOperator) && (!selectedField.choices || selectedField.choices.length === 0)) {
        return (
          <Input
            type={selectedField.attributeType === FieldType.EMAIL ? 'email' : 'text'}
            value={value?.toString() || ''}
            onChange={(e) => setValue(e.target.value)}
            placeholder={selectedField.attributeType === FieldType.EMAIL ? 'Enter email...' : 'Enter text...'}
            className="w-full"
          />
        );
      }
    }

    // Default fallback - basic text input
    return (
      <Input
        value={value?.toString() || ''}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter value..."
        className="w-full"
      />
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 overflow-y-auto space-y-2 pb-20">
        <div className="flex flex-col items-start gap-2 px-0 py-0 relative">
          <div className="items-center justify-center gap-2.5 flex relative self-stretch w-full flex-[0_0_auto]">
            <div className="relative flex-1 mt-[-1.00px] font-medium text-[#16282d] text-sm tracking-[0] leading-[22px]">
              Run action if
            </div>
          </div>
          <div className="flex-col items-start gap-1 flex relative self-stretch w-full flex-[0_0_auto]">
            <Button
              variant="outline"
              className={`inline-flex flex-col items-center gap-2 px-3 py-1 relative flex-[0_0_auto] h-auto rounded-lg border border-solid ${logicalOperator === 'AND' ? 'bg-grey-0 border-[#16282d]' : 'bg-grey-0 border-grey-2'
                }`}
              onClick={() => setLogicalOperator('AND')}
            >
              <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                <div className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                  All conditions match
                </div>
              </div>
              {logicalOperator === 'AND' && (
                <div className="inline-flex items-center justify-center absolute top-[-18px] -right-4  rounded-lg  p-2 m-0">
                  <CheckCircle className="w-5 h-5 text-white" fill="#16282d" />
                </div>
              )}
            </Button>
            <Button
              variant="outline"
              className={`inline-flex flex-col items-center gap-2 px-3 py-1 relative flex-[0_0_auto] h-auto rounded-lg border border-solid ${logicalOperator === 'OR' ? 'bg-grey-0 border-[#16282d]' : 'bg-grey-0 border-grey-2'
                }`}
              onClick={() => setLogicalOperator('OR')}
            >
              <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                <div className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                  At least one condition matches
                </div>
              </div>
              {logicalOperator === 'OR' && (
                <div className="inline-flex items-center justify-center absolute top-[-18px] -right-4  rounded-lg  p-2 m-0">
                  <CheckCircle className="w-5 h-5 text-white" fill="#16282d" />
                </div>
              )}
            </Button>
          </div>
        </div>

        {/* Existing unlocked conditions list - locked conditions are not shown for editing */}
        <div>
          <Label className="relative self-stretch mt-[-1.00px] z-[2] font-medium text-grey-6 text-sm tracking-[0] leading-[22px]">
            Conditions
          </Label>
        </div>
        {conditions.filter((c) => !c.locked).length > 0 && (
          <div className="flex flex-col items-start gap-2 px-0 py-0 relative">
            <div className="space-y-1 w-full">
              {conditions.filter((condition) => !condition.locked).map((condition) => {
                // If this condition is being edited, show the editing form instead
                if (editingConditionId === condition.uuid) {
                  return (
                    <Card
                      key={condition.uuid}
                      className="relative self-stretch w-full flex-[0_0_auto] z-[1] bg-grey-0 rounded-2xl border border-solid border-[#e5e8eb]"
                    >
                      <CardContent className="flex flex-col items-center justify-center gap-2 px-0 py-3">
                        <div className="flex flex-col items-start gap-2 px-3 py-0 self-stretch w-full relative flex-[0_0_auto] space-y-1">
                          {/* Step 1: Group Selection */}
                          <div className="space-y-1 w-full">
                            <Label className="text-sm font-medium text-gray-700">Condition Type</Label>
                            <Select value={selectedGroup || ''} onValueChange={onChangeGroup}>
                              <SelectTrigger className="w-full overflow-hidden max-w-full">
                                <SelectValue placeholder="Select Condition Type" className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
                              </SelectTrigger>
                              <SelectContent>
                                {groupsForVariable.map((group) => (
                                  <SelectItem key={group} value={group}>
                                    {group}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Step 2: Field Selection (only show after group is selected) */}
                          {selectedGroup && fieldsForGroup.length > 0 && (
                            <div className="space-y-1 w-full">
                              {/* <Label className="text-sm font-medium text-gray-700">Data Field</Label> */}
                              <Select value={selectedFieldId || ''} onValueChange={onChangeField}>
                                <SelectTrigger className="w-full overflow-hidden max-w-full">
                                  <SelectValue placeholder="Select Data Field" className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
                                </SelectTrigger>
                                <SelectContent className="max-w-[300px]">
                                  {fieldsForGroup.map((field) => (
                                    <SelectItem key={field.id} value={field.id}>
                                      {convertToTitleCase(field.displayName)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Step 2: Operation Selection (only show after field is selected) */}
                          {selectedFieldId && (
                            <div className="space-y-1 w-full">
                              <Select value={selectedOperator || ''} onValueChange={onChangeOperator}>
                                <SelectTrigger className="w-full overflow-hidden max-w-full">
                                  <SelectValue placeholder="Select Operation" className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
                                </SelectTrigger>
                                <SelectContent>
                                  {operators.map((operator) => (
                                    <SelectItem key={operator.value} value={operator.value}>
                                      {operator.operatorLabel}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Step 3: Value Selection (only show after operation is selected) */}
                          {selectedOperator && (
                            <div className="space-y-1 w-full">
                              {renderValueInput()}
                            </div>
                          )}

                          {validationError && (
                            <div className="text-sm text-red-500">
                              {validationError}
                            </div>
                          )}

                          <div className="flex items-center justify-between px-0 py-0 self-stretch w-full relative flex-[0_0_auto]">
                            <div className={`flex items-center gap-2 ${!isEditingExisting ? 'ml-auto' : ''}`}>
                              <Button variant="outline" size="xs" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                              <Button size="xs" disabled={!isFormComplete()} onClick={handleSaveCondition}>
                                Add
                              </Button>
                            </div>
                            {/* Delete button for editing existing conditions */}
                            <Button
                              variant="outline"
                              size="smallIcon"
                              onClick={() => {
                                handleDeleteCondition(condition.uuid);
                                setEditingConditionId(null);
                              }}
                              className="text-red-5 border-red-5 hover:bg-red-1"
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }

                // Otherwise, show the normal condition display
                return (
                  <div
                    key={condition.uuid}
                    className="group relative w-full z-[1] bg-grey-0 rounded-2xl border border-solid border-[#e5e8eb] flex items-center justify-between px-4 py-3 hover:bg-grey-1 transition-colors"
                  >
                    {/* Simple text display matching Figma */}
                    <div className="text-sm text-grey-6 font-normal flex-1 pr-4">
                      {formatConditionText(condition, conditionOptions)}
                    </div>

                    {/* Action buttons - only visible on hover */}
                    <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex w-6 h-6 items-center justify-center p-0 hover:bg-grey-2"
                        onClick={() => {
                          setEditingConditionId(condition.uuid);
                        }}
                      >
                        <FontAwesomeIcon icon={faPen} className="h-3 w-3 text-grey-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex w-6 h-6 items-center justify-center p-0 hover:bg-red-50"
                        onClick={() => handleDeleteCondition(condition.uuid)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3 text-grey-6" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add condition button */}
        {!isAddingNewCondition && !isEditingExisting && (
          <Button
            variant="outline"
            className="flex flex-col items-center gap-2 px-4 py-[5px] relative self-stretch w-full flex-[0_0_auto] z-0 bg-grey-0 rounded-lg border border-dashed border-grey-3 h-auto"
            onClick={() => setEditingConditionId('new')}
          >
            <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
              <Plus className="h-3 w-3 text-grey-6" />
              <span className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                Add Condition
              </span>
            </div>
          </Button>
        )}

        {/* Condition editing form - only for new conditions */}
        {isAddingNewCondition && (
          <Card className="relative self-stretch w-full flex-[0_0_auto] z-[1] bg-grey-0 rounded-2xl border border-solid border-[#e5e8eb]">
            <CardContent className="flex flex-col items-center justify-center gap-2 px-0 py-3">
              <div className="flex flex-col items-start gap-2 px-3 py-0 self-stretch w-full relative flex-[0_0_auto] space-y-1">
                {/* Step 1: Group Selection */}
                <div className="space-y-1 w-full">
                  <Label className="text-sm font-medium text-gray-700">Condition Type</Label>
                  <Select value={selectedGroup || ''} onValueChange={onChangeGroup}>
                    <SelectTrigger className="w-full overflow-hidden max-w-full">
                      <SelectValue placeholder="Select Condition Type" className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
                    </SelectTrigger>
                    <SelectContent>
                      {groupsForVariable.map((group) => (
                        <SelectItem key={group} value={group}>
                          {group}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 2: Field Selection (only show after group is selected) */}
                {selectedGroup && fieldsForGroup.length > 0 && (
                  <div className="space-y-1 w-full">
                    {/* <Label className="text-sm font-medium text-gray-700">Data Field</Label> */}
                    <Select value={selectedFieldId || ''} onValueChange={onChangeField}>
                      <SelectTrigger className="w-full overflow-hidden max-w-full">
                        <SelectValue placeholder="Select Data Field" className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[300px]">
                        {fieldsForGroup.map((field) => (
                          <SelectItem key={field.id} value={field.id}>
                            {convertToTitleCase(field.displayName)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Step 2: Operation Selection (only show after field is selected) */}
                {selectedFieldId && (
                  <div className="space-y-1 w-full">
                    <Select value={selectedOperator || ''} onValueChange={onChangeOperator}>
                      <SelectTrigger className="w-full overflow-hidden max-w-full">
                        <SelectValue placeholder="Select Operation" className="truncate overflow-hidden max-w-full text-ellipsis whitespace-nowrap" />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map((operator) => (
                          <SelectItem key={operator.value} value={operator.value}>
                            {operator.operatorLabel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Step 3: Value Selection (only show after operation is selected) */}
                {selectedOperator && (
                  <div className="space-y-1 w-full">
                    {renderValueInput()}
                  </div>
                )}

                {validationError && (
                  <div className="text-sm text-red-500">
                    {validationError}
                  </div>
                )}

                <div className="flex items-center justify-between px-0 py-0 self-stretch w-full relative flex-[0_0_auto]">
                  {/* Delete button for editing existing conditions */}
                  {isEditingExisting && (
                    <Button
                      variant="outline"
                      size="smallIcon"
                      onClick={() => {
                        handleDeleteCondition(editingConditionId!);
                        setEditingConditionId(null);
                      }}
                      className="text-red-5 border-red-5 hover:bg-red-1"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                    </Button>
                  )}
                  <div className={`flex items-center gap-2 ${!isEditingExisting ? 'ml-auto' : ''}`}>
                    <Button variant="outline" size="xs" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                    <Button size="xs" disabled={!isFormComplete()} onClick={handleSaveCondition}>
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start gap-2 p-4 border-t bg-white shadow-lg z-[99]">
        <Button
          onClick={handleDone}
          disabled={conditions.filter((c) => !c.locked).length === 0 && lockedConditions.length === 0}
          variant="outline"
        >
          Done
        </Button>
      </div>
    </div>
  );
};
